import React, { useEffect, useState } from "react";
import { AlertTriangle, RefreshCcw } from "lucide-react";
import { useParams } from "react-router-dom";
import FieldRenderer from "../components/studentDetails/FieldRenderer";
import Loader from "../components/studentDetails/Loader";
import StudentCard from "../components/studentDetails/StudentCard";
import { getStudentByIdentifier, StudentQrError } from "../services/studentService";
import "../styles/StudentDetails.css";
import { buildStudentSections, extractStudentSummary } from "../utils/studentDetails";

function ErrorState({ message, onRetry }) {
  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="student-empty-shell student-fade-in text-center">
        <div className="student-state-icon mx-auto">
          <AlertTriangle size={28} strokeWidth={2.2} />
        </div>
        <h2 className="mt-5 text-2xl font-bold text-slate-900">
          Unable to load student details
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-base leading-7 text-slate-600">
          {message}
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
        >
          <RefreshCcw size={16} strokeWidth={2.2} />
          Try again
        </button>
      </div>
    </div>
  );
}

export default function StudentDetails() {
  const { studentId, uid } = useParams();
  const resolvedStudentId = String(studentId || uid || "").trim();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let isMounted = true;

    async function fetchStudent() {
      if (!resolvedStudentId) {
        setStudent(null);
        setErrorMessage("A student ID is required in the URL.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setErrorMessage("");

      try {
        const studentRecord = await getStudentByIdentifier(resolvedStudentId);

        if (!isMounted) {
          return;
        }

        setStudent(studentRecord);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        console.error("Failed to fetch student details:", error);

        if (error instanceof StudentQrError && error.code === "student-not-found") {
          setErrorMessage(
            `No student record was found for "${resolvedStudentId}". Please verify the link and try again.`,
          );
        } else {
          setErrorMessage(
            "The student record could not be fetched from Firebase right now. Please retry in a moment.",
          );
        }

        setStudent(null);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    fetchStudent();

    return () => {
      isMounted = false;
    };
  }, [resolvedStudentId, reloadToken]);

  if (loading) {
    return (
      <div className="student-details-page">
        <Loader />
      </div>
    );
  }

  if (errorMessage || !student) {
    return (
      <div className="student-details-page">
        <ErrorState
          message={errorMessage || "Student details are not available."}
          onRetry={() => setReloadToken((currentValue) => currentValue + 1)}
        />
      </div>
    );
  }

  const summary = extractStudentSummary(student, resolvedStudentId);
  const sections = buildStudentSections(student, summary.omittedKeys);

  return (
    <div className="student-details-page">
      <div className="student-page-shell mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="student-page-orb student-page-orb-left" aria-hidden="true" />
        <div className="student-page-orb student-page-orb-right" aria-hidden="true" />

        <StudentCard summary={summary} />

        <div className="mt-8 grid gap-6 xl:grid-cols-2">
          {sections.map((section, index) => (
            <section
              key={section.key}
              className="student-section-card student-fade-in"
              style={{ animationDelay: `${120 * (index + 1)}ms` }}
            >
              <div className="student-section-heading">
                <h2 className="student-section-title">{section.title}</h2>
                <span className="student-section-count">{section.fields.length}</span>
              </div>

              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                {section.fields.map((field) => (
                  <FieldRenderer
                    key={field.key}
                    label={field.label}
                    value={field.value}
                    path={field.key}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>

        {sections.length === 0 && (
          <div className="mt-8 student-empty-shell student-fade-in text-center">
            <h2 className="text-2xl font-bold text-slate-900">No extra details found</h2>
            <p className="mt-3 text-base leading-7 text-slate-600">
              The profile loaded successfully, but there are no additional student fields to display.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
